import { useEffect, useState } from "react";

// When the same element has multiple of the same kind of event listener,
// the order in which they execute is non-deterministic.
// Furthermore, having these event listener on the same element
// (i.e. all on window) seems to cause problems when rerenders are triggered by
// the event handler and some of the listeners never get called.
//
// This problem seems to be avoided by instead having different listeners
// attach to different elements (i.e. one on window and one on document).
// This also gives us the option of having a heirarchy and having the ability
// to therefore control the order of listener execution and stop
// propogation (i.e. with event.stopPropagation).
function useClickOutsideElement(
  elementRef: React.RefObject<HTMLElement>,
  action: () => void,
  bindingCondition: boolean,
  bindTarget: Window | Document,
) {
  useEffect(() => {
    function handleClick(event: Event) {
      if (
        elementRef.current &&
        !elementRef.current.contains(event.target as Node)
      ) {
        // Don't fire if clicking inside a FloatingPortal (nested dropdown).
        // Portal content renders outside #root as a direct child of body.
        const appRoot = document.getElementById("root");
        if (
          appRoot &&
          !appRoot.contains(event.target as Node)
        ) {
          return;
        }
        action();
      }
    }

    if (bindingCondition) {
      bindTarget.addEventListener("mousedown", handleClick);
      return () => {
        // Unbind the event listener on clean up,
        // which runs every time dependencies change.
        bindTarget.removeEventListener("mousedown", handleClick);
      };
    }
  }, [elementRef, action, bindingCondition, bindTarget]);
}

function useKeyDown<T>(
  elementRef: React.RefObject<T>,
  shouldListen: boolean,
  action: (this: Window, ev: KeyboardEvent) => void,
) {
  useEffect(() => {
    if (shouldListen) {
      window.addEventListener("keydown", action);
    } else {
      window.removeEventListener("keydown", action);
    }
    return () => {
      // Unbind the event listener on clean up,
      //   does nothing if the event has already been removed
      window.removeEventListener("keydown", action);
    };
  }, [elementRef, shouldListen, action]);
}

function useKeyUp(
  elementRef: React.RefObject<HTMLElement>,
  shouldListen: boolean,
  action: (this: Window, ev: KeyboardEvent) => void,
) {
  useEffect(() => {
    if (shouldListen) {
      window.addEventListener("keyup", action);
    } else {
      window.removeEventListener("keyup", action);
    }
    return () => {
      // Unbind the event listener on clean up,
      //   does nothing if the event has already been removed
      window.removeEventListener("keyup", action);
    };
  }, [elementRef, shouldListen, action]);
}

function useIsDev(): boolean {
  return process.env.NODE_ENV === "development";
}

/** Forces a re-render every 60 seconds so relative timestamps stay fresh. */
function useMinuteTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  return tick;
}

function useWindowWidth(): number {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
}

export {
  useClickOutsideElement,
  useKeyDown,
  useKeyUp,
  useIsDev,
  useMinuteTick,
  useWindowWidth,
};
